import { BALANCE, damageForTile, type BoardMerge, type Direction } from '@2k48/shared';
import { feedback } from '../../platform/feedback';
import { CombatEffects, type CombatSide } from '../CombatEffects';
import { mountGameArea, type GameAreaHandle } from '../GameArea';
import { renderLoop } from '../RenderLoop';
import { onDebugEffect, type DebugEffect } from '../debug/effects';
import { DEBUG_ENABLED } from '../debug/metrics';
import { attachTransitionFeedback } from '../transitionFeedback';
import { LocalBattle, type LocalBattleSnapshot } from './LocalBattle';

export interface LocalGameUiState extends LocalBattleSnapshot {
  readonly remainingMs: number;
}

export interface LocalGameOptions {
  seed?: string;
  combat?: boolean;
  /** Null makes the board untimed, as in classic solo play. */
  roundMs?: number | null;
  /** Full view root. Required when combat effects should target player panels. */
  combatRoot?: HTMLElement;
  onState?(state: LocalGameUiState): void;
}

export interface LocalGameHandle {
  readonly battle: LocalBattle;
  readonly area: GameAreaHandle;
  readonly combat: CombatEffects | null;
  triggerDebug(effect: DebugEffect): void;
  destroy(): void;
}

/**
 * Local vertical slice used by Solo and by the offline Versus laboratory.
 *
 * It intentionally mirrors the eventual network seam: rules produce a board
 * snapshot plus transitions, the persistent renderer consumes transitions,
 * and the small reactive HUD consumes only the snapshot. Replacing LocalBattle
 * with authoritative patches later does not require replacing the renderer.
 */
export function mountLocalGame(
  container: HTMLElement,
  options: LocalGameOptions = {},
): LocalGameHandle {
  const combatEnabled = options.combat ?? false;
  const battle = new LocalBattle({
    seed: options.seed ?? `local-${Date.now()}`,
    combat: combatEnabled,
  });
  let latest = battle.snapshot;
  let displayedPlayerHp = latest.playerHp;
  let displayedOpponentHp = latest.opponentHp;
  const roundMs = options.roundMs ?? null;
  let remainingMs = roundMs ?? 0;
  let lastPaintedSecond = Math.ceil(remainingMs / 1000);
  const startedAt = performance.now();
  let destroyed = false;

  let combat: CombatEffects | null = null;

  const emit = (): void => {
    if (destroyed) return;
    const visibleStatus = displayedPlayerHp === 0 || displayedOpponentHp === 0 ? 'ko' : 'playing';
    options.onState?.({
      ...latest,
      playerHp: displayedPlayerHp,
      opponentHp: displayedOpponentHp,
      status: visibleStatus,
      remainingMs,
    });
  };

  const settleAnimations = (): void => {
    // Advancing the board can start the attack attached to its final frame;
    // finishing combat second guarantees the HUD reaches the same state.
    area.renderer.finishRunning();
    combat?.finishRunning();
  };

  const commitHp = (side: CombatSide, hp: number): void => {
    if (side === 'p1') displayedPlayerHp = hp;
    else displayedOpponentHp = hp;
    emit();
  };

  const playKoIfNeeded = (side: CombatSide, hp: number): void => {
    if (hp !== 0 || !combat) return;
    combat.playKo(side, undefined, () => feedback.emit('match.ko', { haptic: side === 'p1' }));
  };

  const playMoveCombat = (
    result: ReturnType<LocalBattle['move']>,
    source: BoardMerge | undefined,
  ): void => {
    if (!combat || !combatEnabled) return;
    const attackDamage = result.damage?.damage ?? 0;
    const penalty = result.stalledPenalty;

    const playPenalty = (): void => {
      if (penalty <= 0) {
        playKoIfNeeded('p2', result.snapshot.opponentHp);
        return;
      }
      combat?.playHit({
        target: 'p1',
        damage: penalty,
        hpFrom: result.previous.playerHp,
        hpTo: result.snapshot.playerHp,
        onImpact: () => feedback.emit('attack.impact'),
        onHpCommit: () => commitHp('p1', result.snapshot.playerHp),
        onComplete: () => playKoIfNeeded('p1', result.snapshot.playerHp),
      });
    };

    if (attackDamage <= 0) {
      playPenalty();
      return;
    }

    const powerValue = powerFrom(result.damage?.powerTiles ?? []);
    combat.playAttack({
      attacker: 'p1',
      target: 'p2',
      damage: attackDamage,
      hpFrom: result.previous.opponentHp,
      hpTo: result.snapshot.opponentHp,
      ...(source ? { sourceTileId: source.survivorId } : {}),
      ...(powerValue ? { powerValue } : {}),
      onCharge: () => feedback.emit('attack.charge'),
      // The simulated opponent is not this device: keep the hit audible but
      // reserve the strong vibration for damage received by P1.
      onImpact: () => feedback.emit('attack.impact', { haptic: false }),
      onHpCommit: () => commitHp('p2', result.snapshot.opponentHp),
      onComplete: playPenalty,
    });
  };

  const onDirection = (direction: Direction): void => {
    settleAnimations();
    const result = battle.move(direction);
    latest = result.snapshot;
    if (!result.accepted) {
      feedback.emit('ui.invalid');
      emit();
      return;
    }

    const source = highestMerge(result.merges);
    const timeline = area.apply(result.transitions);
    attachTransitionFeedback(timeline, result.transitions);

    if (combatEnabled && (result.damage?.damage ?? 0) > 0) {
      displayedOpponentHp = result.previous.opponentHp;
    }
    if (combatEnabled && result.stalledPenalty > 0) {
      displayedPlayerHp = result.previous.playerHp;
    }
    emit();

    timeline.call(() => playMoveCombat(result, source));
    if (result.snapshot.status === 'ko') area.input.setEnabled(false);
  };

  const area = mountGameArea(container, {
    cols: BALANCE.board.size,
    rows: BALANCE.board.size,
    defaultTone: 'p1',
    onDirection,
  });
  area.sync(toEntities(latest));

  if (options.combatRoot) {
    combat = new CombatEffects(options.combatRoot, area.renderer.root);
  }

  const unsubscribeClock =
    roundMs === null
      ? null
      : renderLoop.subscribe(() => {
          const next = Math.max(0, roundMs - (performance.now() - startedAt));
          const second = Math.ceil(next / 1000);
          if (second === lastPaintedSecond) return;
          lastPaintedSecond = second;
          remainingMs = next;
          if (next === 0) {
            settleAnimations();
            area.input.setEnabled(false);
          }
          emit();
        });

  const triggerDebug = (effect: DebugEffect): void => {
    if (!combat) return;
    settleAnimations();

    if (effect === 'hit') {
      const result = battle.damagePlayer(10);
      latest = result.snapshot;
      displayedPlayerHp = result.previous.playerHp;
      emit();
      combat.playHit({
        target: 'p1',
        damage: 10,
        hpFrom: result.previous.playerHp,
        hpTo: result.snapshot.playerHp,
        onHpCommit: () => commitHp('p1', result.snapshot.playerHp),
        onComplete: () => playKoIfNeeded('p1', result.snapshot.playerHp),
      });
      return;
    }

    if (effect === 'ko') {
      const result = battle.damageOpponent(BALANCE.versus.startingHp);
      latest = result.snapshot;
      displayedOpponentHp = 0;
      emit();
      area.input.setEnabled(false);
      combat.playKo('p2');
      return;
    }

    const value = debugValue(effect);
    const damage = damageForTile(value);
    const result = battle.damageOpponent(damage);
    latest = result.snapshot;
    displayedOpponentHp = result.previous.opponentHp;
    emit();
    const powerValue = value >= 512 ? (value as 512 | 1024 | 2048) : undefined;
    combat.playAttack({
      attacker: 'p1',
      target: 'p2',
      damage,
      hpFrom: result.previous.opponentHp,
      hpTo: result.snapshot.opponentHp,
      sourceTileId: highestTileId(result.snapshot),
      ...(powerValue ? { powerValue } : {}),
      onHpCommit: () => commitHp('p2', result.snapshot.opponentHp),
      onComplete: () => playKoIfNeeded('p2', result.snapshot.opponentHp),
    });
  };

  const unsubscribeDebug = DEBUG_ENABLED ? onDebugEffect(triggerDebug) : null;
  emit();

  return {
    battle,
    area,
    get combat() {
      return combat;
    },
    triggerDebug,
    destroy() {
      destroyed = true;
      unsubscribeClock?.();
      unsubscribeDebug?.();
      combat?.destroy();
      combat = null;
      area.destroy();
    },
  };
}

function toEntities(snapshot: LocalBattleSnapshot) {
  return snapshot.board.tiles.map((tile) => ({
    id: tile.id,
    cell: tile.cell,
    value: tile.value,
    tone: 'p1',
  }));
}

function highestMerge(merges: readonly BoardMerge[]): BoardMerge | undefined {
  return merges.reduce<BoardMerge | undefined>(
    (highest, merge) => (!highest || merge.value > highest.value ? merge : highest),
    undefined,
  );
}

function powerFrom(values: readonly number[]): 512 | 1024 | 2048 | undefined {
  const value = values.reduce((highest, current) => Math.max(highest, current), 0);
  return value === 512 || value === 1024 || value === 2048 ? value : undefined;
}

function debugValue(effect: Exclude<DebugEffect, 'hit' | 'ko'>): number {
  switch (effect) {
    case 'attack-8':
      return 8;
    case 'attack-64':
      return 64;
    case 'power-512':
      return 512;
    case 'power-1024':
      return 1024;
    case 'power-2048':
      return 2048;
  }
}

function highestTileId(snapshot: LocalBattleSnapshot): string | undefined {
  return snapshot.board.tiles.reduce<(typeof snapshot.board.tiles)[number] | undefined>(
    (highest, tile) => (!highest || tile.value > highest.value ? tile : highest),
    undefined,
  )?.id;
}
